<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ProposalsAPI from 'dashboard/api/proposals';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  proposal: { type: Object, required: true },
});
const { t } = useI18n();
const payments = ref([]);
const canCreate = ref(false);
const configured = ref(false);
const loading = ref(true);
const saving = ref(false);
const checking = ref(null);
const errorMessage = ref('');
const retryDates = ref({});
const recurrence = ref('one_time');
const today = new Date().toISOString().slice(0, 10);
const month = ref(today.slice(0, 7));
const year = ref(today.slice(0, 4));
const dueDate = ref(today);
const inputClass =
  'rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12';
const recurrences = computed(() =>
  Object.keys(props.proposal.totals_by_recurrence || {}).filter(
    key => Number(props.proposal.totals_by_recurrence[key]) > 0
  )
);
const amount = computed(
  () => props.proposal.totals_by_recurrence?.[recurrence.value] || 0
);
const money = value =>
  Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const recurrenceLabel = key =>
  ({
    one_time: t('CATALOG_PROPOSALS.RECURRENCE.one_time'),
    monthly: t('CATALOG_PROPOSALS.RECURRENCE.monthly'),
    yearly: t('CATALOG_PROPOSALS.RECURRENCE.yearly'),
  })[key];
const statusLabel = status =>
  ({
    pending: t('PROPOSAL_PAYMENTS.PENDING'),
    overdue: t('PROPOSAL_PAYMENTS.OVERDUE'),
    paid: t('PROPOSAL_PAYMENTS.PAID'),
    cancelled: t('PROPOSAL_PAYMENTS.CANCELLED'),
    refunded: t('PROPOSAL_PAYMENTS.REFUNDED'),
    refund_pending: t('PROPOSAL_PAYMENTS.REFUND_PENDING'),
  })[status];

const load = async () => {
  const { data } = await ProposalsAPI.payments(props.proposal.id);
  payments.value = data.payments;
  canCreate.value = data.can_create;
  configured.value = data.configured;
};

const showError = error => {
  errorMessage.value =
    error.response?.data?.error ||
    error.response?.data?.message ||
    t('PROPOSAL_PAYMENTS.ERROR');
};

const submitPayment = async payload => {
  if (saving.value) return;
  saving.value = true;
  errorMessage.value = '';
  try {
    await ProposalsAPI.createPayment(props.proposal.id, payload);
  } catch (error) {
    showError(error);
  } finally {
    try {
      await load();
    } catch (error) {
      showError(error);
    }
    saving.value = false;
  }
};

const createPayment = () =>
  submitPayment({
    recurrence: recurrence.value,
    billing_period: recurrence.value === 'monthly' ? month.value : year.value,
    due_date: dueDate.value,
  });

const retryPayment = payment =>
  submitPayment({
    recurrence: payment.recurrence,
    billing_period: payment.billing_period,
    due_date: retryDates.value[payment.id] || today,
  });

const checkPayment = async payment => {
  checking.value = payment.id;
  errorMessage.value = '';
  try {
    const { data } = await ProposalsAPI.refreshPayment(
      props.proposal.id,
      payment.id
    );
    Object.assign(payment, data);
  } catch (error) {
    showError(error);
  } finally {
    checking.value = null;
  }
};

const copyPix = async payment => {
  try {
    await copyTextToClipboard(payment.pix_payload);
    useAlert(t('PROPOSAL_PAYMENTS.COPIED'));
  } catch (error) {
    showError(error);
  }
};

onMounted(async () => {
  recurrence.value = recurrences.value[0] || 'one_time';
  try {
    await load();
  } catch (error) {
    showError(error);
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section class="w-full border-t border-n-weak pt-3">
    <h4 class="text-sm font-semibold text-n-slate-12">
      {{ t('PROPOSAL_PAYMENTS.TITLE') }}
    </h4>
    <p class="my-2 text-xs text-n-slate-11">
      {{ t('PROPOSAL_PAYMENTS.HINT') }}
    </p>
    <p v-if="errorMessage" role="alert" class="my-2 text-sm text-n-ruby-11">
      {{ errorMessage }}
    </p>
    <p v-if="!loading && !configured" class="text-sm text-n-slate-11">
      {{ t('PROPOSAL_PAYMENTS.CONFIGURE') }}
    </p>
    <form
      v-if="canCreate && configured && recurrences.length"
      class="my-3 flex flex-wrap items-end gap-3"
      @submit.prevent="createPayment"
    >
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('PROPOSAL_PAYMENTS.RECURRENCE') }}
        <select v-model="recurrence" :class="inputClass" :disabled="saving">
          <option v-for="key in recurrences" :key="key" :value="key">
            {{ recurrenceLabel(key) }}
          </option>
        </select>
      </label>
      <label
        v-if="recurrence !== 'one_time'"
        class="flex flex-col gap-1 text-xs text-n-slate-11"
      >
        {{ t('PROPOSAL_PAYMENTS.PERIOD') }}
        <input
          v-if="recurrence === 'monthly'"
          v-model="month"
          type="month"
          required
          :class="inputClass"
          :disabled="saving"
        />
        <input
          v-else
          v-model="year"
          type="number"
          min="2000"
          max="9999"
          required
          :class="inputClass"
          :disabled="saving"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('PROPOSAL_PAYMENTS.DUE_DATE') }}
        <input
          v-model="dueDate"
          type="date"
          :min="today"
          required
          :class="inputClass"
          :disabled="saving"
        />
      </label>
      <Button
        type="submit"
        :label="t('PROPOSAL_PAYMENTS.CREATE', { amount: money(amount) })"
        :is-loading="saving"
      />
    </form>
    <div
      v-for="payment in payments"
      :key="payment.id"
      class="my-2 rounded-lg border border-n-weak p-3"
    >
      <p class="text-sm font-medium text-n-slate-12">
        {{
          t('PROPOSAL_PAYMENTS.SUMMARY', {
            amount: money(payment.value),
            period:
              payment.recurrence === 'one_time'
                ? recurrenceLabel('one_time')
                : payment.billing_period,
            status: statusLabel(payment.status),
          })
        }}
      </p>
      <p class="text-xs text-n-slate-11">
        {{ t('PROPOSAL_PAYMENTS.DUE', { date: payment.due_date }) }}
      </p>
      <p
        v-if="payment.gateway_state !== 'ready'"
        class="my-2 text-xs text-n-slate-11"
      >
        {{
          payment.gateway_state === 'failed'
            ? t('PROPOSAL_PAYMENTS.FAILED')
            : t('PROPOSAL_PAYMENTS.UNRESOLVED')
        }}
      </p>
      <form
        v-if="payment.can_retry && canCreate && configured"
        class="mt-3 flex flex-wrap items-end gap-3"
        @submit.prevent="retryPayment(payment)"
      >
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('PROPOSAL_PAYMENTS.RETRY_DUE') }}
          <input
            :value="retryDates[payment.id] || today"
            type="date"
            :min="today"
            required
            :class="inputClass"
            :disabled="saving || checking !== null"
            @input="retryDates[payment.id] = $event.target.value"
          />
        </label>
        <Button
          type="submit"
          size="sm"
          :label="t('PROPOSAL_PAYMENTS.RETRY')"
          :is-loading="saving"
          :disabled="checking !== null"
        />
      </form>
      <div class="mt-2 flex flex-wrap gap-2">
        <Button
          v-if="
            payment.pix_payload &&
            ['pending', 'overdue'].includes(payment.status)
          "
          size="sm"
          variant="faded"
          :label="t('PROPOSAL_PAYMENTS.COPY')"
          @click="copyPix(payment)"
        />
        <Button
          v-if="canCreate && configured && !payment.can_retry"
          size="sm"
          variant="ghost"
          :label="t('PROPOSAL_PAYMENTS.CHECK')"
          :is-loading="checking === payment.id"
          :disabled="checking !== null || saving"
          @click="checkPayment(payment)"
        />
      </div>
    </div>
  </section>
</template>
